import Tooltip from '@/components/UI/Tooltip';
import { formatNumber, isNumericString } from '@/helpers';
import { _notNil } from '@/littledash';
import type { Nullable } from '@/model/Common.model';
import cn from 'classnames';
import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import type { DataTableCellIndexCoordinate } from '@/components/UI/DataTableV2/DataTable.model';
import styles from '../DataTableV2.module.scss';
import { useDataTable } from '../DataTableProvider';
import { useCellAlerts } from '../hooks/useCellAlerts';
import { useCellCoordinate } from '../hooks/useCellCoordinate';
import { useCellData } from '../hooks/useCellData';
import { useCellEditability } from '../hooks/useCellEditability';
import { useCellEditMode } from '../hooks/useCellEditMode';
import { useCellMouseHandlers } from '../hooks/useCellMouseHandlers';
import { useCellPositioning } from '../hooks/useCellPositioning';
import { useCellScrollLock } from '../hooks/useCellScrollLock';
import { useCellSelectionState } from '../hooks/useCellSelectionState';
import { useCellState } from '../hooks/useCellState';

const formatCellValue = (value: string, active: boolean): string =>
  !active && isNumericString(value) ? `${formatNumber(value)}` : value;
const updateCellValue = (element: HTMLDivElement, value: Nullable<string>) => {
  element.innerText = formatCellValue(value ?? '', element.getAttribute('data-active') === 'true');
};

interface DataTableDefaultCellProps {
  coordinate: DataTableCellIndexCoordinate;
  positionOffset: { x: number; y: number };
}

export const DataTableDefaultCell: FC<DataTableDefaultCellProps> = ({ coordinate, positionOffset }) => {
  const { dataTableService } = useDataTable();
  const { column, row } = coordinate;
  const inputRef = useRef<HTMLDivElement>(null);

  // Foundation
  const { idCoordinate, cellRef } = useCellCoordinate(coordinate);

  // Data & State
  const { cellData } = useCellData(idCoordinate, cellRef);
  const { cellState, tooltip } = useCellState(idCoordinate, cellRef);
  const { selectionState } = useCellSelectionState(coordinate, idCoordinate);
  const { hasAlert } = useCellAlerts(idCoordinate, cellRef);
  const { isEditable, isColumnLocked, isColumnReadonly } = useCellEditability(coordinate);

  // Interaction
  const { handleMouseDown, handleMouseEnter, handleMouseUp, handleDoubleClick } = useCellMouseHandlers(coordinate, {
    onDoubleClick: () => {
      // Early return for readonly/locked columns
      if (!isEditable) {
        return;
      }

      if (inputRef.current) {
        inputRef.current.contentEditable = 'true';
        const selection = window.getSelection();
        const range = document.createRange();
        selection?.removeAllRanges();
        range.selectNodeContents(inputRef.current);
        range.collapse(false);
        selection?.addRange(range);
        inputRef.current.focus();
      }
    },
  });

  const { active, handleBlur, handleKeyDown } = useCellEditMode(coordinate, idCoordinate, inputRef, {
    editable: isEditable,
  });

  // Side effects
  useCellScrollLock(active);

  // Styling
  const { containerStyle, contentStyle } = useCellPositioning(positionOffset, active);

  const cellType = dataTableService.columnByIndex(coordinate?.column)?.type;

  // Initialize and update cell display value
  useEffect(() => {
    if (_notNil(inputRef.current) && _notNil(idCoordinate)) {
      updateCellValue(inputRef.current, cellData?.value);
    }
  }, [cellData, idCoordinate]);

  const hasGroupBorder = dataTableService.rightBorderType(coordinate.column) === 'group-separator';

  return (
    <div
      className={cn(styles.CellContainer, { [styles.groupBorder]: hasGroupBorder })}
      data-test-component="DataTableDefaultCell"
      data-test-element="container"
      data-test-key={`(${column},${row})`}
      style={containerStyle}
    >
      <Tooltip render={tooltip}>
        <div
          ref={inputRef}
          className={cn(styles.Cell, styles[selectionState], { [styles.Active]: active }, styles[cellState], {
            [styles.ReadonlyColumn]: isColumnLocked || isColumnReadonly,
          })}
          data-celltype={cellType}
          data-state={cellState}
          data-active={active ? 'true' : 'false'}
          data-test-component="DataTableDefaultCell"
          data-test-element="content"
          style={contentStyle}
          onMouseDown={handleMouseDown}
          onMouseEnter={handleMouseEnter}
          onMouseUp={handleMouseUp}
          onDoubleClick={handleDoubleClick}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          contentEditable={active}
        >
          {formatCellValue(cellData?.value ?? '', active)}
        </div>
      </Tooltip>
      {hasAlert && <div className={styles.CellAlert} data-test-element="alert-chip" />}
    </div>
  );
};
